import React, { useState, useRef, useEffect, useId } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption<T extends string = string> {
  id: T;
  label: string;
}

export interface CollapsibleSelectProps<T extends string = string> {
  options: readonly SelectOption<T>[] | SelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  className?: string;
  id?: string;
}

export function CollapsibleSelect<T extends string = string>({
  options,
  value,
  onChange,
  label,
  className = '',
  id,
}: CollapsibleSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const generatedId = useId();
  const baseId = id || generatedId;
  const triggerId = `${baseId}-trigger`;
  const listboxId = `${baseId}-listbox`;

  const selectedOption = options.find((opt) => opt.id === value) || options[0];

  // Close on click outside and escape key
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (optionId: T) => {
    onChange(optionId);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        // Focus active or first option next tick
        setTimeout(() => {
          const selectedIdx = options.findIndex((opt) => opt.id === value);
          const idxToFocus = selectedIdx >= 0 ? selectedIdx : 0;
          optionsRef.current[idxToFocus]?.focus();
        }, 30);
      } else {
        setIsOpen(false);
      }
    }
  };

  const handleOptionKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    currentIndex: number
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = (currentIndex + 1) % options.length;
      optionsRef.current[nextIndex]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prevIndex = (currentIndex - 1 + options.length) % options.length;
      optionsRef.current[prevIndex]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      optionsRef.current[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      optionsRef.current[options.length - 1]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      triggerRef.current?.focus();
    } else if (e.key === 'Tab') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`w-full relative ${className}`}>
      {label && (
        <label
          htmlFor={triggerId}
          className="block text-xs font-semibold text-[#A3A3A3] uppercase tracking-wider mb-1.5"
        >
          {label}
        </label>
      )}

      {/* Collapsed Trigger Button */}
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        onClick={() => setIsOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        className="w-full h-12 min-h-[48px] px-4 rounded-xl bg-[#1C1C1C] border border-[#262626] text-[#FAFAFA] hover:border-[#525252] focus-visible:border-[#525252] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#525252] flex items-center justify-between text-sm sm:text-[15px] font-medium transition-colors cursor-pointer select-none active:scale-[0.99]"
      >
        <span className="truncate">{selectedOption?.label}</span>
        <ChevronDown
          className={`w-4 h-4 text-[#A3A3A3] shrink-0 transition-transform duration-200 motion-reduce:transition-none ${
            isOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>

      {/* Expanded Options Panel */}
      <div
        className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out motion-reduce:transition-none ${
          isOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 pointer-events-none'
        }`}
      >
        <div className="overflow-hidden">
          <div
            id={listboxId}
            role="listbox"
            aria-labelledby={triggerId}
            className="p-1.5 rounded-xl bg-[#141414] border border-[#262626] shadow-[0_10px_25px_rgba(0,0,0,0.4)] flex flex-col gap-1"
          >
            {options.map((opt, index) => {
              const isSelected = opt.id === value;
              return (
                <button
                  key={opt.id}
                  ref={(el) => {
                    optionsRef.current[index] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={isOpen ? 0 : -1}
                  onClick={() => handleSelect(opt.id)}
                  onKeyDown={(e) => handleOptionKeyDown(e, index)}
                  className={`w-full min-h-[44px] px-3.5 py-2.5 rounded-lg text-sm text-left flex items-center justify-between transition-colors cursor-pointer select-none ${
                    isSelected
                      ? 'bg-[#1C1C1C] border border-[#525252] text-[#FAFAFA] font-semibold'
                      : 'text-[#A3A3A3] hover:bg-[#262626] hover:text-[#FAFAFA] font-medium border border-transparent'
                  }`}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSelected && (
                    <Check
                      className="w-4 h-4 text-[#F97316] shrink-0 ml-2"
                      aria-hidden="true"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
